import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users, Search, Plus, MoreHorizontal, ShieldCheck, ShieldAlert, KeyRound,
  UserX, Activity, Loader2, ChevronLeft, ChevronRight, Trash2,
  SlidersHorizontal, Gauge, ScrollText, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { adminAPI, legalAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

const ROLES = ['compliance', 'procurement', 'regulatory', 'legal_entry', 'admin', 'super_admin'];

// [Phase 3(c) Stage 2] The exact three-boolean pr_government_business shape
// registration writes and the Ley 40 applicability predicate reads.
const PR_GOV_FIELDS = [
  ['does_business_with_pr_government', 'Does business with / contracts with the PR government'],
  ['provides_public_services_or_contracts', 'Provides public services or holds public contracts'],
  ['handles_governmental_data_or_ict', 'Handles governmental data or ICT'],
];

function formatDate(iso) {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; }
}

export function UserManagementTab() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);

  // Create user form
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', entity_type: 'Individual', entity_name: '', entity_url: '', role: 'compliance' });

  // [Phase 3(c) Stage 2] Gate-profile / scan-allowance / audit state (super_admin)
  const [sectorList, setSectorList] = useState([]);         // db.legal_sectors rows
  const [gateOpen, setGateOpen] = useState(false);
  const [gateProfile, setGateProfile] = useState(null);     // fetched gate-profile
  const [gateSectors, setGateSectors] = useState([]);       // selected name_en strings
  const [gatePrGov, setGatePrGov] = useState({});
  const [gateSaving, setGateSaving] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantForm, setGrantForm] = useState({ reset_first_scan: false, reset_monthly_usage: false, set_limit: false, scans_limit: 0 });
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  // Load the authoritative sector list once (labels by name_en; stored value is
  // the name_en string the applicability predicate matches — never the id).
  useEffect(() => {
    legalAPI.listSectors(true).then(res => setSectorList(res.data.sectors || [])).catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listUsers({ search, role: roleFilter, limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreateUser = async () => {
    try {
      await adminAPI.createUser(createForm);
      toast.success('User created');
      setCreateOpen(false);
      setCreateForm({ name: '', email: '', password: '', entity_type: 'Individual', entity_name: '', entity_url: '', role: 'compliance' });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await adminAPI.updateUser(selectedUser.id, { role: newRole });
      toast.success(`Role changed to ${newRole}`);
      setRoleDialogOpen(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to change role'); }
  };

  // [Phase 3(c) Stage 2] Open the gate-profile editor for a user.
  const handleOpenGate = async (u) => {
    setSelectedUser(u);
    setGateProfile(null);
    setGateOpen(true);
    try {
      const res = await adminAPI.getGateProfile(u.id);
      setGateProfile(res.data);
      setGateSectors(res.data.cisa_sectors || []);
      setGatePrGov(res.data.pr_government_business || {});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load profile'); }
  };

  const toggleGateSector = (nameEn) => {
    setGateSectors(prev => prev.includes(nameEn) ? prev.filter(s => s !== nameEn) : [...prev, nameEn]);
  };

  const handleSaveGate = async () => {
    if (!selectedUser) return;
    setGateSaving(true);
    try {
      await adminAPI.updateGateProfile(selectedUser.id, {
        cisa_sectors: gateSectors,
        pr_government_business: {
          does_business_with_pr_government: !!gatePrGov.does_business_with_pr_government,
          provides_public_services_or_contracts: !!gatePrGov.provides_public_services_or_contracts,
          handles_governmental_data_or_ict: !!gatePrGov.handles_governmental_data_or_ict,
        },
      });
      toast.success('Gate profile updated');
      setGateOpen(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    finally { setGateSaving(false); }
  };

  // [Phase 3(c) Stage 2] Scan-allowance grant/reset (freemium testing lever).
  const handleOpenGrant = async (u) => {
    setSelectedUser(u);
    setGrantForm({ reset_first_scan: false, reset_monthly_usage: false, set_limit: false, scans_limit: 0 });
    setGrantOpen(true);
    try {
      const res = await adminAPI.getGateProfile(u.id);
      setGateProfile(res.data);
      setGrantForm(f => ({ ...f, scans_limit: res.data.subscription?.scans_limit ?? 0 }));
    } catch { /* non-fatal */ }
  };

  const handleGrantScan = async () => {
    if (!selectedUser) return;
    const payload = {
      reset_first_scan: grantForm.reset_first_scan,
      reset_monthly_usage: grantForm.reset_monthly_usage,
    };
    if (grantForm.set_limit) payload.scans_limit = Number(grantForm.scans_limit);
    if (!payload.reset_first_scan && !payload.reset_monthly_usage && !grantForm.set_limit) {
      toast.error('Select at least one change'); return;
    }
    try {
      const res = await adminAPI.grantScan(selectedUser.id, payload);
      toast.success(`Scan allowance updated (${(res.data.changed || []).join(', ')})`);
      setGrantOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update allowance'); }
  };

  // [Phase 3(c) Stage 2] Per-field admin audit trail for a user.
  const handleOpenAudit = async (u) => {
    setSelectedUser(u);
    setAuditLogs([]);
    setAuditOpen(true);
    try {
      const res = await adminAPI.getAuditLog({ target_user_id: u.id, limit: 200 });
      setAuditLogs(res.data.audit || []);
    } catch { setAuditLogs([]); }
  };

  const handleDeactivate = async (u) => {
    const isActive = u.registration_status !== 'deactivated';
    try {
      await adminAPI.updateUser(u.id, { is_active: !isActive });
      toast.success(isActive ? 'User deactivated' : 'User reactivated');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    try {
      await adminAPI.resetUserPassword(selectedUser.id, newPassword ? { new_password: newPassword } : {});
      toast.success(newPassword ? 'Password reset' : 'Reset email sent');
      setResetDialogOpen(false);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleViewActivity = async (u) => {
    setSelectedUser(u);
    setActivityDialogOpen(true);
    try {
      const res = await adminAPI.getUserActivity(u.id);
      setActivityLogs(res.data.activity || []);
    } catch { setActivityLogs([]); }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await adminAPI.deleteUser(selectedUser.id);
      toast.success('User permanently deleted');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
  };

  const allowedRoles = isSuperAdmin ? ROLES : ROLES.filter(r => r !== 'super_admin' && r !== 'admin');

  return (
    <div className="space-y-4" data-testid="admin-user-management">
      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by name, email, or entity..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="pl-9 h-10 bg-muted/30 border-border" data-testid="admin-user-search" />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v === 'all' ? '' : v); setPage(0); }}>
          <SelectTrigger className="w-[160px] h-10 bg-muted/30 border-border" data-testid="admin-role-filter">
            <SelectValue placeholder="All roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setCreateOpen(true)} data-testid="admin-create-user-button">
          <Plus className="mr-2 h-4 w-4" />Create User
        </Button>
      </div>

      {/* Users Table */}
      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Email</TableHead>
                <TableHead className="text-xs">Entity</TableHead>
                <TableHead className="text-xs">Role</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Verified</TableHead>
                <TableHead className="text-xs">Created</TableHead>
                <TableHead className="text-xs w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
              ) : users.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No users found</TableCell></TableRow>
              ) : users.map(u => (
                <TableRow key={u.id} data-testid="admin-user-row" className="hover:bg-muted/20">
                  <TableCell className="font-medium text-sm">{u.name || '\u2014'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="text-sm">{u.entity_name || '\u2014'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] capitalize">{u.role || 'unassigned'}</Badge></TableCell>
                  <TableCell>
                    <Badge variant={u.registration_status === 'active' ? 'outline' : 'secondary'} className={cn('text-[10px]', u.registration_status === 'active' ? 'border-vrs-green/30 text-vrs-green' : u.registration_status === 'deactivated' ? 'border-vrs-red/30 text-vrs-red' : 'text-muted-foreground')}>
                      {u.registration_status || 'pending'}
                    </Badge>
                  </TableCell>
                  <TableCell>{u.email_verified ? <ShieldCheck className="h-4 w-4 text-vrs-green" /> : <ShieldAlert className="h-4 w-4 text-muted-foreground" />}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{formatDate(u.created_at)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="admin-user-actions">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* [Phase 3(c) Stage 2] Self-role-edit disabled (lockout prevention).
                            [Stage 3.5, DEF-F2/UI-A] Role editing is super_admin-only
                            ([A20]/DEC-011, server-enforced 403); hide the control for
                            non-super-admins so no dead affordance is shown. */}
                        {isSuperAdmin && (
                          <DropdownMenuItem disabled={u.id === currentUser?.id} onClick={() => { setSelectedUser(u); setNewRole(u.role || ''); setRoleDialogOpen(true); }} data-testid="admin-change-role">
                            <Users className="mr-2 h-4 w-4" />Change Role
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => { setSelectedUser(u); setResetDialogOpen(true); }}>
                          <KeyRound className="mr-2 h-4 w-4" />Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewActivity(u)}>
                          <Activity className="mr-2 h-4 w-4" />View Activity
                        </DropdownMenuItem>
                        {/* [Phase 3(c) Stage 2] super_admin gate-profile / allowance / audit */}
                        {isSuperAdmin && (
                          <>
                            <DropdownMenuItem onClick={() => handleOpenGate(u)} data-testid="admin-edit-gate-profile">
                              <SlidersHorizontal className="mr-2 h-4 w-4" />Edit Compliance Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenGrant(u)} data-testid="admin-scan-allowance">
                              <Gauge className="mr-2 h-4 w-4" />Scan Allowance
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenAudit(u)} data-testid="admin-audit-trail">
                              <ScrollText className="mr-2 h-4 w-4" />Audit Trail
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeactivate(u)} className={u.registration_status === 'deactivated' ? 'text-vrs-green' : 'text-destructive'}>
                          <UserX className="mr-2 h-4 w-4" />{u.registration_status === 'deactivated' ? 'Reactivate' : 'Deactivate'}
                        </DropdownMenuItem>
                        {isSuperAdmin && u.id !== currentUser?.id && (
                          <DropdownMenuItem onClick={() => { setSelectedUser(u); setDeleteDialogOpen(true); }} className="text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />Delete Permanently
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</p>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create User</DialogTitle><DialogDescription>Manually create a new user account (bypasses registration).</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} data-testid="create-user-name" /></div>
            <div className="space-y-1"><Label>Email *</Label><Input type="email" value={createForm.email} onChange={e => setCreateForm({...createForm, email: e.target.value})} data-testid="create-user-email" /></div>
            <div className="space-y-1"><Label>Password *</Label><Input type="password" value={createForm.password} onChange={e => setCreateForm({...createForm, password: e.target.value})} data-testid="create-user-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Entity Name</Label><Input value={createForm.entity_name} onChange={e => setCreateForm({...createForm, entity_name: e.target.value})} /></div>
              <div className="space-y-1">
                <Label>Role</Label>
                <Select value={createForm.role} onValueChange={v => setCreateForm({...createForm, role: v})}>
                  <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{allowedRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={handleCreateUser} data-testid="create-user-submit">Create User</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change Role</DialogTitle><DialogDescription>Change role for {selectedUser?.name || selectedUser?.email}</DialogDescription></DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger data-testid="change-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>{allowedRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          {/* [Phase 3(c) Stage 2] Scan-gating implications notice */}
          {newRole && newRole !== selectedUser?.role && (
            <div className="flex items-start gap-2 rounded-lg border border-vrs-amber/30 bg-vrs-amber/5 px-3 py-2">
              <AlertTriangle className="h-4 w-4 text-vrs-amber mt-0.5 shrink-0" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Role affects scan gating: <span className="font-medium">regulatory / admin / super_admin</span> scan for free;{' '}
                <span className="font-medium">compliance</span> gets one free scan then a paywall;{' '}
                <span className="font-medium">procurement</span> needs a paid subscription. Confirm this is intended.
              </p>
            </div>
          )}
          <DialogFooter><Button onClick={handleChangeRole} data-testid="change-role-submit">Confirm Role Change</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Phase 3(c) Stage 2] Edit Compliance (gate) Profile Dialog — super_admin */}
      <Dialog open={gateOpen} onOpenChange={setGateOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Compliance Profile</DialogTitle>
            <DialogDescription>{selectedUser?.name || selectedUser?.email} — edits the data the applicability engine reads (sectors, PR-government triggers).</DialogDescription>
          </DialogHeader>
          {!gateProfile ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">CISA Sectors</Label>
                <div className="grid grid-cols-1 gap-1.5 max-h-56 overflow-y-auto rounded-lg border border-border/60 p-2">
                  {sectorList.map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5">
                      <Checkbox checked={gateSectors.includes(s.name_en)} onCheckedChange={() => toggleGateSector(s.name_en)} data-testid={`gate-sector-${s.id}`} />
                      <span>{s.name_en}{s.is_universal && ' *'}</span>
                    </label>
                  ))}
                  {sectorList.length === 0 && <p className="text-xs text-muted-foreground px-1">No sectors loaded.</p>}
                </div>
                <p className="text-[10px] text-muted-foreground">Stored as sector names (the values the predicate matches), not ids.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">PR Government Business</Label>
                {PR_GOV_FIELDS.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2">
                    <span className="text-xs">{label}</span>
                    <Switch checked={!!gatePrGov[key]} onCheckedChange={v => setGatePrGov(prev => ({ ...prev, [key]: v }))} data-testid={`gate-prgov-${key}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGateOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveGate} disabled={gateSaving || !gateProfile} data-testid="gate-profile-save">
              {gateSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Phase 3(c) Stage 2] Scan Allowance Dialog — super_admin */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Scan Allowance</DialogTitle>
            <DialogDescription>{selectedUser?.name || selectedUser?.email} — freemium testing/support lever. No billing change.</DialogDescription>
          </DialogHeader>
          {gateProfile && (
            <div className="rounded-lg border border-border/60 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
              <p>Plan: <span className="font-mono">{gateProfile.subscription?.plan}</span> · Status: <span className="font-mono">{gateProfile.subscription?.status || '—'}</span></p>
              <p>Free scan used: <span className="font-mono">{String(gateProfile.first_scan_used)}</span> · This month: <span className="font-mono">{gateProfile.subscription?.scans_used_this_month}/{gateProfile.subscription?.scans_limit}</span></p>
              {/* [Stage 3.5, DEF-F3/UI-B] Tell the admin which lever governs THIS
                  user's plan so the wrong one is not selected (Stage 2 smoke ERR). */}
              <p className="pt-0.5" data-testid="grant-operative-lever">
                Operative lever for this plan:{' '}
                <span className="font-medium text-foreground">
                  {(gateProfile.subscription?.plan || 'free') === 'free' ? 'Reset free scan' : 'Monthly usage / limit'}
                </span>
              </p>
            </div>
          )}
          {/* [Stage 3.5, DEF-F3/UI-B] Each lever is tagged operative vs. not-for-
              this-plan; UI clarity only, no enforcement change. */}
          {(() => { const freePlan = (gateProfile?.subscription?.plan || 'free') === 'free'; return (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={grantForm.reset_first_scan} onCheckedChange={v => setGrantForm(f => ({ ...f, reset_first_scan: !!v }))} data-testid="grant-reset-first" />
              <span>Reset free scan (re-grant the one free scan)</span>
              <span className="text-[10px] text-muted-foreground">{freePlan ? '(operative for this plan)' : '(applies to free plans)'}</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={grantForm.reset_monthly_usage} onCheckedChange={v => setGrantForm(f => ({ ...f, reset_monthly_usage: !!v }))} data-testid="grant-reset-monthly" />
              <span>Reset monthly usage to 0</span>
              <span className="text-[10px] text-muted-foreground">{freePlan ? '(applies to paid plans)' : '(operative for this plan)'}</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={grantForm.set_limit} onCheckedChange={v => setGrantForm(f => ({ ...f, set_limit: !!v }))} data-testid="grant-set-limit" />
              <span>Set monthly scan limit</span>
              <span className="text-[10px] text-muted-foreground">{freePlan ? '(applies to paid plans)' : '(operative for this plan)'}</span>
            </label>
            {grantForm.set_limit && (
              <Input type="number" min={0} value={grantForm.scans_limit} onChange={e => setGrantForm(f => ({ ...f, scans_limit: e.target.value }))} className="h-9" data-testid="grant-limit-input" />
            )}
          </div>
          ); })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button onClick={handleGrantScan} data-testid="grant-scan-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* [Phase 3(c) Stage 2] Admin Audit Trail Dialog — super_admin */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Admin Audit Trail</DialogTitle>
            <DialogDescription>{selectedUser?.name || selectedUser?.email} — every admin edit to this user (append-only).</DialogDescription>
          </DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No admin edits recorded</p>
          ) : (
            <div className="space-y-2">
              {auditLogs.map(rec => (
                <div key={rec.id} className="flex items-start gap-3 rounded-lg border border-border/60 p-3">
                  <ScrollText className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium font-mono">{rec.field}</p>
                    <p className="text-xs text-muted-foreground break-words">
                      <span className="text-vrs-red">{JSON.stringify(rec.old)}</span>{' → '}
                      <span className="text-vrs-green">{JSON.stringify(rec.new)}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{rec.at} · by {rec.admin_user_id}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>For {selectedUser?.email}. Leave blank to send a reset email.</DialogDescription></DialogHeader>
          <Input type="password" placeholder="New password (optional)" value={newPassword} onChange={e => setNewPassword(e.target.value)} data-testid="reset-password-input" />
          <DialogFooter><Button onClick={handleResetPassword} data-testid="reset-password-submit">{newPassword ? 'Set Password' : 'Send Reset Email'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Activity Log</DialogTitle><DialogDescription>{selectedUser?.name || selectedUser?.email}</DialogDescription></DialogHeader>
          {activityLogs.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No activity recorded</p> : (
            <div className="space-y-2">
              {activityLogs.map(log => (
                <div key={log.id} className="flex items-start gap-3 rounded-lg border border-border/60 p-3">
                  <Activity className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium capitalize">{log.action_type.replace(/_/g, ' ')}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">{log.timestamp}</p>
                    {log.metadata && Object.keys(log.metadata).length > 0 && <p className="text-xs text-muted-foreground mt-1">{JSON.stringify(log.metadata)}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{selectedUser?.email}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} data-testid="confirm-delete-user">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
